import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Check, Loader2, RefreshCw } from "lucide-react";
import { FaviconManagementDialog } from "./FaviconManagementDialog";

interface Favicon {
  id: string;
  name: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
}

function setFavicons(url: string) {
  const existingLinks = document.querySelectorAll(
    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
  );
  existingLinks.forEach(link => link.remove());

  const cacheBustedUrl = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;

  const iconLink = document.createElement("link");
  iconLink.rel = "icon";
  iconLink.type = "image/png";
  iconLink.href = cacheBustedUrl;
  document.head.appendChild(iconLink);

  const shortcutLink = document.createElement("link");
  shortcutLink.rel = "shortcut icon";
  shortcutLink.href = cacheBustedUrl;
  document.head.appendChild(shortcutLink);

  const appleTouchIcon = document.createElement("link");
  appleTouchIcon.rel = "apple-touch-icon";
  appleTouchIcon.href = cacheBustedUrl;
  document.head.appendChild(appleTouchIcon);
}

export function FaviconManagement() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFavicon, setSelectedFavicon] = useState<Favicon | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const { data: favicons, isLoading } = useQuery({
    queryKey: ["admin-favicons"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("favicons")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Favicon[];
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (faviconId: string) => {
      setActivatingId(faviconId);
      // Deactivate all favicons first
      const { error: deactivateError } = await supabase
        .from("favicons")
        .update({ is_active: false })
        .neq("id", faviconId);
      if (deactivateError) throw deactivateError;

      // Activate the selected favicon
      const { error: activateError } = await supabase
        .from("favicons")
        .update({ is_active: true })
        .eq("id", faviconId);
      if (activateError) throw activateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-favicons"] });
      queryClient.invalidateQueries({ queryKey: ["active-favicon"] });
      toast.success("Favicon activated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    onSettled: () => {
      setActivatingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (faviconId: string) => {
      const { error } = await supabase
        .from("favicons")
        .delete()
        .eq("id", faviconId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-favicons"] });
      queryClient.invalidateQueries({ queryKey: ["active-favicon"] });
      toast.success("Favicon deleted");
      setDeleteDialogOpen(false);
      setSelectedFavicon(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleRefreshFavicon = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["active-favicon"] });
      const activeFavicon = favicons?.find(f => f.is_active);
      if (activeFavicon?.image_url) {
        setFavicons(activeFavicon.image_url);
      }
      toast.success("Favicon refreshed");
    } catch (error) {
      toast.error("Failed to refresh favicon");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Favicon</CardTitle>
          <CardDescription>
            Manage the browser tab icon for your site.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefreshFavicon}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button onClick={() => { setSelectedFavicon(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Favicon
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {favicons && favicons.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {favicons.map((favicon) => (
                <TableRow key={favicon.id}>
                  <TableCell>
                    <img
                      src={favicon.image_url}
                      alt={favicon.name}
                      className="h-8 w-8 object-contain border rounded"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{favicon.name}</TableCell>
                  <TableCell>
                    {favicon.is_active ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {!favicon.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => activateMutation.mutate(favicon.id)}
                          disabled={activatingId === favicon.id}
                        >
                          {activatingId === favicon.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedFavicon(favicon);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedFavicon(favicon);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No favicons found. Add your first favicon to get started.
          </div>
        )}
      </CardContent>

      <FaviconManagementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        favicon={selectedFavicon}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Favicon</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedFavicon?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedFavicon && deleteMutation.mutate(selectedFavicon.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
